import { Suspense, lazy, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Activity, AlertCircle, BarChart3, Briefcase, Calendar, CheckCircle2,
  ChevronLeft, ChevronRight, Clock, Download, FileText, Layout as LayoutIcon, Library, Loader2, MapPin, Package,
  Plane, Receipt, Save, Settings, Stamp, TrendingUp, Users, Wallet, X,
} from 'lucide-react';

// Modul-Embed-Widgets (Klon-Funktion, Joerg 2026-05-29): lazy import der
// Modul-Pages, damit sie nicht ins UebersichtFuehrung-Chunk wandern.
const EmbeddedTasksPage         = lazy(() => import('bauberichteV2/pages/TasksPage'));
const EmbeddedReportsPage       = lazy(() => import('bauberichteV2/pages/ReportsPage'));
const EmbeddedPlantafelPage     = lazy(() => import('plantafel/pages/BoardPage'));
const EmbeddedMaterialPage      = lazy(() => import('stammdaten/pages/MaterialStammPage'));
const EmbeddedBibliothekPage    = lazy(() => import('bibliothek/pages/LibraryPage'));
const EmbeddedAdressbuchPage    = lazy(() => import('./UebersichtAdressbuch'));
const EmbeddedKundenPage        = lazy(() => import('./UebersichtKunden'));
const EmbeddedZeiterfassungPage = lazy(() => import('./UebersichtZeiterfassung'));
const EmbeddedBaustellePage     = lazy(() => import('./UebersichtBaustelle'));
const EmbeddedAuswertungenPage  = lazy(() => import('./UebersichtAuswertungen'));
// Verwaltungs-Sub-Tabs als Embed-Widgets (Joerg 2026-05-30) — gleiche
// Komponenten wie UebersichtVerwaltung.tsx, hier als eigenstaendige Widgets.
const EmbeddedGlossarAdminPage    = lazy(() => import('bauberichteV2/pages/GlossarAdminPage'));
const EmbeddedVorlagenListPage    = lazy(() => import('../../modules/vorlagen/pages/VorlagenListPage'));
const EmbeddedMobileHomepagePage  = lazy(() => import('./MobileHomepageAdminPage'));
// Joerg 2026-05-30: Tages-Karte als Widget — TagesMapView lazy importiert,
// damit leaflet+CSS nicht in den UebersichtFuehrung-Chunk wandern.
const LazyTagesMapView = lazy(() => import('plantafel/public').then((m) => ({ default: m.TagesMapView })));
import { api } from '@onvice/portal-shell-api';
import { getHolidays, type RegionCode } from '@onvice/portal-ui';
import WidgetGrid, { type Layout } from '../../components/WidgetGrid';
import PageSettingsDialog from '../../components/PageSettingsDialog';
import { InfoTooltip } from '../../components/InfoTooltip';
import { PresenceDot } from '../../lib/presence-store';
import { HomeCard, type HomeCardData } from '../../components/HomeCard';
// Diese vier Konstanten/Helfer sind Kopie aus zeiterfassung/frontend/src/lib/api.ts.
// Kopie statt Federation-Import, damit der Portal-Boot NICHT von zeiterfassung/
// remoteEntry.js abhaengt — sonst kippt ein einzelner Container-Ausfall den
// gesamten Boot (Bootstrap-Preload). Regel: [[feedback_federation_no_static_imports]].
// Bei Aenderungen in zeiterfassung/lib/api.ts hier synchron mitpflegen.
type AbsenceType = 'urlaub' | 'krank' | 'sonderurlaub' | 'unbezahlt' | 'schule' | 'sonstiges' | 'zeitausgleich';
const ABSENCE_TYPE_LABEL: Record<AbsenceType, string> = {
  urlaub: 'Urlaub',
  krank: 'Krank',
  sonderurlaub: 'Sonderurlaub',
  unbezahlt: 'Unbezahlt',
  schule: 'Schule/Fortbildung',
  sonstiges: 'Sonstiges',
  zeitausgleich: 'Zeitausgleich (Plus abfeiern)',
};
const ABSENCE_TYPE_ICON: Record<AbsenceType, string> = {
  urlaub: 'Plane',
  krank: 'Stethoscope',
  sonderurlaub: 'Sparkles',
  unbezahlt: 'CircleDollarSign',
  schule: 'GraduationCap',
  sonstiges: 'Circle',
  zeitausgleich: 'TimerReset',
};
const ABSENCE_TYPE_COLOR: Record<AbsenceType, string> = {
  urlaub: 'text-amber-300',
  krank: 'text-rose-300',
  sonderurlaub: 'text-violet-300',
  unbezahlt: 'text-brand-300',
  schule: 'text-sky-300',
  sonstiges: 'text-slate-300',
  zeitausgleich: 'text-teal-300',
};
function fmtMinutes(m: number | null | undefined): string {
  if (m == null) return '–';
  const sign = m < 0 ? '-' : '';
  const abs = Math.abs(m);
  const h = Math.floor(abs / 60);
  const r = abs % 60;
  return `${sign}${h}:${String(r).padStart(2, '0')}`;
}
// Inline-Types (2026-08-02): plantafel-Modul wurde in eigenen Federation-Container
// migriert, der Local-Path existiert nicht mehr. Federation-Alias-Import waere ein
// Runtime-Chunk-Fetch bei jedem Boot — inline-Types sind zero-Runtime und stabil
// gegen Modul-Container-Ausfaelle.
type PlantafelUser = { id: string; color?: string | null };
type PlantafelTask = Record<string, unknown> & { id: string };
import { colorForPerson } from '@onvice/portal-ui';
import { LucideByName } from '@onvice/portal-ui';
import { Circle } from 'lucide-react';

type Absence = { name: string; type: string; absenceId: string };

function AbsenceRow({ absence }: { absence: Absence }) {
  const type = absence.type as keyof typeof ABSENCE_TYPE_ICON;
  const iconName = ABSENCE_TYPE_ICON[type] ?? 'Circle';
  const colorCls = ABSENCE_TYPE_COLOR[type] ?? 'text-slate-300';
  const label = ABSENCE_TYPE_LABEL[type] ?? absence.type;
  return (
    <li>
      <Link
        to={`/m/zeiterfassung/antraege/${absence.absenceId}`}
        className="flex items-center gap-2 text-muted-foreground hover:bg-card/40 rounded px-1 -mx-1 py-0.5 transition-colors"
      >
        <span className={colorCls}>
          <LucideByName name={iconName} fallback={Circle} size={12} />
        </span>
        <span className="truncate">{absence.name}</span>
        <span className="text-xs">{label}</span>
      </Link>
    </li>
  );
}

/**
 * Fuehrung-Sicht — historisch GF-Startseite mit 17 Widgets.
 *
 * Seit 2026-05-26 (Joerg-Pivot) doppeldeutig:
 * - Legacy ohne URL-Param: feste pageSlug='fuehrung' (Hetzner-Bestandsstand).
 * - Neuer Modus mit URL-Param :id: laedt eine persoenliche User-Page
 *   (portal_user_pages) und zeigt deren Layout. Header zeigt page.name.
 *
 * Datenfluss bleibt: /portal/dashboard/fuehrung Roundtrip + 15s-Polling.
 * Datenquelle: identisch fuer beide Modi. Permission auf das Aggregat-
 * Endpoint (Backend prueft auf Achse leitung/admin) gilt fuer beide.
 *
 * Konzept: 04 Ressourcen/Softwarearchitektur/Widget-Picker-Konzept.md
 */

interface UserPage {
  id: string;
  name: string;
  icon: string | null;
  sort_order: number;
  layout_json: Layout;
  source_template_id: string | null;
}

export interface DashboardData {
  mannschaft: {
    aktiv: number; aktivNamen?: string[]; eingeplant: number;
    aktivList?: Array<{
      userId: string; name: string;
      /** Joerg 2026-07-24: Persistente Person-Farbe aus addressbook.persons.color. */
      color?: string | null;
      bruttoMinutesToday: number; breakMinutesToday: number;
      taskMinutesToday: number;
    }>;
    krank: Array<{ name: string; type: string; absenceId: string }>;
    urlaub: Array<{ name: string; type: string; absenceId: string }>;
    // 2026-07-23 (Joerg): seit = Zeitpunkt des pause_start-Stempels (ISO),
    // damit die Card „in Pause seit HH:MM (X min)" anzeigen kann. null nur
    // als Absicherung wenn ein alter Payload noch die String-Form liefert.
    inPause?: Array<{ name: string; since: string | null }>;
    nichtGestempelt: string[];
  };
  laufendeTasks: Array<{ id: string; title: string; customer: string | null; user: string | null }>;
  offenSummary: { eigeneBerichte: number; fremdeBerichte: number; zeitkorrekturen: number; urlaubsantraege: number; aufgabenOhneOwner: number; total: number };
  eigeneBerichteOffen: Array<{ id: string; title: string; customer: string | null; submittedAt: string | null }>;
  fremdeBerichteOffen: Array<{ id: string; title: string; customer: string | null; user: string | null; submittedAt: string | null }>;
  fremdeBerichteVerifiziert: Array<{ id: string; title: string; user: string | null; verifiedAt: string | null }>;
  fremdeBerichteVerifiziertTotal: number;
  zeitkorrekturen: Array<{
    id: string; user: string | null; createdAt: string;
    requestedType: string | null;
    requestedOccurredAt: string | null;
    reason: string | null;
    originalType: string | null;
    originalOccurredAt: string | null;
  }>;
  urlaubsantraege: Array<{
    // ABW 2026-08-28: NULL = ganztaegig; sonst Stunden-Zeitausgleich in Minuten.
    durationMinutes: number | null;
    id: string; user: string | null; type: string;
    fromDate: string; toDate: string; createdAt: string;
    urlaubstage: number;
    resturlaubTage: number | null;
    ueberzieht: boolean | null;
  }>;
  aufgabenOhneOwner: Array<{ id: string; title: string; customer: string | null }>;
  rechnungenEntwurf: Array<{ id: string; title: string; customer: string | null; brutto: number | null; createdBy: string | null; createdAt: string }>;
  rechnungenEntwurfTotal: number;
  rechnungenGenehmigung: Array<{ id: string; title: string; customer: string | null; brutto: number | null; createdBy: string | null; createdAt: string }>;
  rechnungenGenehmigungTotal: number;
  stundenWoche: { stunden: number; sparkline: Array<{ week: string; stunden: number }>; vorjahrStunden: number | null };
  topKundenStunden: Array<{ id: string; name: string; stunden: number }>;
  materialMonat: Array<{ name: string; menge: number; einheit: string | null; kosten: number }>;
  letzte10Berichte: Array<{ id: string; title: string; customer: string | null; user: string | null; createdAt: string }>;
  letzteStatusWechsel: Array<{ id: string; title: string; status: string | null; updatedAt: string }>;
  wochenVorschau: Array<{ date: string; count: number; abnahmen: Array<{ id: string; title: string; customer: string | null }>; feiertag: string | null }>;
}

const POLL_MS = 15_000;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function formatEuro(value: number | null | undefined): string {
  if (value == null) return '—';
  return value.toLocaleString('de-DE', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
}

function formatStunden(value: number): string {
  return value.toLocaleString('de-DE', { maximumFractionDigits: 0 });
}

function formatDateShort(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' });
}

function formatDateTimeShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', timeZone: 'Europe/Berlin' })
    + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
}

const STAMP_TYPE_LABEL: Record<string, string> = {
  kommt: 'Komme',
  geht: 'Gehe',
  pause_start: 'Pause an',
  pause_end: 'Pause aus',
};

export default function UebersichtFuehrung() {
  const navigate = useNavigate();
  const params = useParams<{ id?: string; actAsUserId?: string; templateId?: string }>();
  const userPageId = params.id;                          // wenn gesetzt: pageId-Modus
  // actAsUserId-Mode: Admin bearbeitet Seiten eines anderen Users (Joerg 2026-06-05).
  // Lade Pages + Save laeuft dann gegen /admin/users/:userId/pages.
  const actAsUserId = params.actAsUserId ?? null;
  // templateId-Mode: Admin bearbeitet Layout einer Seiten-Vorlage (Joerg 2026-06-08).
  // Lade aus /admin/page-templates/:id, Save geht via templateId-Prop an WidgetGrid
  // (PATCH /admin/page-templates/:id mit { layout }).
  const templateId = params.templateId ?? null;
  const inTemplateMode = !!templateId;
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [region, setRegion] = useState<RegionCode>('NW');
  const [editing, setEditing] = useState(false);
  const [saveTick, setSaveTick] = useState(0);
  const [cancelTick, setCancelTick] = useState(0);
  const [userPage, setUserPage] = useState<UserPage | null>(null);
  const [userPageError, setUserPageError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  // /home/today einmalig laden — speist die operativen Heute-Widgets (siehe widgetMap).
  // Karten kommen permission-gefiltert vom Backend; Widget ohne passende Card rendert
  // ueber HomeCard-Fallback einen Spinner mit Platzhalter-Titel.
  const [todayCards, setTodayCards] = useState<Map<string, HomeCardData> | null>(null);

  // Legacy-Auto-Redirect (Joerg 2026-05-27): wenn der User /uebersicht/fuehrung
  // ohne pageId aufruft, schicken wir ihn auf seine persoenliche GF-Tag-Seite —
  // dort ist Bearbeiten + Reset moeglich. Wenn er gar keine GF-Tag-Seite hat
  // (z.B. nicht-GF), bleibt die Legacy-Sicht als read-only sichtbar.
  useEffect(() => {
    if (userPageId) return; // pageId-Modus
    if (actAsUserId) return; // Admin-Mode hat eigenen Index, kein Auto-Redirect
    if (inTemplateMode) return; // Template-Editor hat eigene Route, kein Redirect
    let aborted = false;
    api
      .get<{ success: boolean; data: Array<{ id: string; source_template_id: string | null }> }>('/portal/my-pages')
      .then((res) => {
        if (aborted || !res.success) return;
        // TEMPLATE_ID_GF aus seed-page-templates.ts
        const TEMPLATE_ID_GF = '00000000-1111-1111-1111-000000000001';
        const gfPage = res.data.find((p) => p.source_template_id === TEMPLATE_ID_GF);
        if (gfPage) navigate(`/uebersicht/page/${gfPage.id}`, { replace: true });
      })
      .catch(() => { /* fail-soft: Legacy-Sicht bleibt sichtbar */ });
    return () => { aborted = true; };
  }, [userPageId, actAsUserId, inTemplateMode, navigate]);

  const load = useCallback((silent = false) => {
    if (!silent) setLoading(true);
    api
      .get<{ success: boolean; data: DashboardData }>('/portal/dashboard/fuehrung')
      .then((res) => {
        if (res.success) { setData(res.data); setError(null); }
        else setError('Dashboard konnte nicht geladen werden');
      })
      .catch((err: unknown) => {
        const status = (err as { status?: number }).status;
        // 403 ist kein harter Fehler mehr (Joerg 2026-05-27): Seite kann auch
        // ohne GF-Daten existieren und nur heute.*-Widgets enthalten. fuehrung.*-
        // Widgets fallen dann durch den widgetMap-Filter raus.
        if (status === 403) { setData(null); setError(null); }
        else setError('Fehler beim Laden');
      })
      .finally(() => { if (!silent) setLoading(false); });
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(() => load(true), POLL_MS);
    return () => window.clearInterval(id);
  }, [load]);

  // Inline-Genehmigen / -Ablehnen (Joerg 2026-05-29): GF kann Urlaubsanträge
  // und Zeitkorrekturen direkt aus der „Mein Tag"-Sicht entscheiden. Ablehnen
  // verlangt einen Pflicht-Kommentar (Modal). Nach Entscheidung wird das
  // Dashboard silent neu geladen — der Eintrag fällt aus der Liste raus.
  const [rejectTarget, setRejectTarget] = useState<{ id: string; kind: 'absence' | 'correction' } | null>(null);
  const [rejectComment, setRejectComment] = useState('');
  const [rejectBusy, setRejectBusy] = useState(false);

  async function decideItem(id: string, kind: 'absence' | 'correction', decision: 'genehmigt' | 'abgelehnt', comment?: string) {
    const path = kind === 'absence' ? `/admin/absences/${id}` : `/admin/corrections/${id}`;
    try {
      const r = await api.put<{ success: boolean; error?: string }>(
        `/modules/zeiterfassung${path}`,
        comment ? { decision, comment } : { decision },
      );
      if (r.success) load(true);
      else console.warn('Entscheidung fehlgeschlagen:', r.error);
    } catch (e) {
      console.error('Entscheidungs-Request fehlgeschlagen:', e);
    }
  }

  const approveItem = useCallback((id: string, kind: 'absence' | 'correction') => {
    return decideItem(id, kind, 'genehmigt');
  }, [load]);

  const openReject = useCallback((id: string, kind: 'absence' | 'correction') => {
    setRejectTarget({ id, kind });
    setRejectComment('');
  }, []);

  async function confirmReject() {
    if (!rejectTarget || rejectBusy) return;
    const comment = rejectComment.trim();
    if (!comment) return;
    setRejectBusy(true);
    try {
      await decideItem(rejectTarget.id, rejectTarget.kind, 'abgelehnt', comment);
      setRejectTarget(null);
    } finally {
      setRejectBusy(false);
    }
  }

  useEffect(() => {
    api
      .get<{ success: boolean; data: { region: RegionCode } }>('/portal/tenant-settings')
      .then((r) => { if (r.success && r.data?.region) setRegion(r.data.region); })
      .catch(() => { /* default NW */ });
  }, []);

  // Heute-Karten einmalig laden — Backend liefert nur Karten zu Modulen die der
  // User hat, alle anderen heute.*-Widgets rendern dann den Fallback-Spinner.
  useEffect(() => {
    api
      .get<{ success: boolean; data: { cards: HomeCardData[] } }>('/portal/home/today')
      .then((r) => {
        if (!r.success) return;
        setTodayCards(new Map(r.data.cards.map((c) => [c.id, c])));
      })
      .catch(() => { /* fail-soft */ });
  }, []);

  // pageId-Modus: User-Page laden (Name + Layout). Im Legacy-Modus uebersprungen.
  // Im actAsUserId-Mode (Admin) lädt der Admin-Endpoint Pages des Ziel-Users.
  useEffect(() => {
    if (inTemplateMode) return;
    if (!userPageId) { setUserPage(null); setUserPageError(null); return; }
    const url = actAsUserId
      ? `/portal/admin/users/${actAsUserId}/pages`
      : '/portal/my-pages';
    api
      .get<{ success: boolean; data: UserPage[] }>(url)
      .then((r) => {
        if (!r.success) { setUserPageError('Seite konnte nicht geladen werden'); return; }
        const found = r.data.find((p) => p.id === userPageId);
        if (!found) { setUserPageError('Seite nicht gefunden'); return; }
        setUserPage(found);
        setUserPageError(null);
      })
      .catch(() => setUserPageError('Fehler beim Laden der Seite'));
  }, [userPageId, actAsUserId, inTemplateMode]);

  // templateId-Mode: Vorlage laden. Wir spiegeln sie in den userPage-State,
  // damit alle Editor-Effekte (initialLayout, Header-Title) zusammenpassen.
  useEffect(() => {
    if (!templateId) return;
    api
      .get<{ success: boolean; data: { id: string; name: string; icon: string | null; layout_json: Layout; is_system: boolean } }>(`/portal/admin/page-templates/${templateId}`)
      .then((r) => {
        if (!r.success) { setUserPageError('Vorlage konnte nicht geladen werden'); return; }
        if (r.data.is_system) { setUserPageError('System-Vorlage ist read-only. Bitte zuerst klonen.'); return; }
        setUserPage({
          id: r.data.id,
          name: r.data.name,
          icon: r.data.icon,
          sort_order: 0,
          layout_json: r.data.layout_json ?? [],
          source_template_id: null,
        });
        setUserPageError(null);
      })
      .catch(() => setUserPageError('Fehler beim Laden der Vorlage'));
  }, [templateId]);

  // Widget-Map: Slug → JSX-Renderer mit Closure ueber data + todayCards.
  // fuehrung.* werden aus dem /dashboard/fuehrung-Aggregat gespeist, heute.*
  // aus /home/today. Widgets fuer die der User kein Backend-Datenobjekt
  // bekommt (z.B. wegen Modul-ACL) rendern den HomeCard-Fallback-Spinner.
  const widgetMap = useMemo<Record<string, (config?: Record<string, unknown>) => ReactNode>>(() => {
    const map: Record<string, (config?: Record<string, unknown>) => ReactNode> = {};

    // Section-Header-Trenner (#5, Joerg/Alex 2026-05-27) — visuelle Anker
    // zwischen Widget-Gruppen. Renderer ist immer aktiv, unabhaengig von data.
    map['fuehrung.section-heute']         = () => renderSectionHeader('Heute live');
    map['fuehrung.section-approvals']     = () => renderSectionHeader('Approvals / To-Do');
    map['fuehrung.section-wirtschaft']    = () => renderSectionHeader('Wirtschaftlicher Puls');
    map['fuehrung.section-aktivitaeten']  = () => renderSectionHeader('Aktivitäten');
    map['fuehrung.section-woche']         = () => renderSectionHeader('Diese Woche');
    map['fuehrung.section-quick']         = () => renderSectionHeader('Schnellzugriffe');
    // Buero-Sections (2026-05-29) — fuer die Persona-Templates Disposition + Abrechnung.
    map['buero.section-disposition']      = () => renderSectionHeader('Disposition');
    map['buero.section-abrechnung']       = () => renderSectionHeader('Abrechnung');
    map['buero.section-wirtschaft']       = () => renderSectionHeader('Wirtschaft');

    // Modul-Embed-Widgets (Klon-Funktion 2026-05-29). config aus
    // layout_json[i].config = { mode: 'full'|'snapshot', filter?: {...} }.
    // Snapshot rendert die Modul-Page im readonly-Mode + Filter eingefroren
    // (wo Modul-Komponente das unterstuetzt — TasksPage tut es, andere Module
    // ignorieren die Props vorerst und rendern wie Voll-Tab); Voll-Tab
    // uebergibt nichts → Standard-Verhalten wie Modul-Tab.
    const embedFallback = (
      <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
    );
    map['aufgaben.embed'] = (config) => {
      const mode = (config?.['mode'] as string | undefined) ?? 'full';
      const filter = config?.['filter'] as { status?: 'aktiv'|'offen'|'in_arbeit'|'pausiert'|'abgeschlossen'|'alle'; owner?: 'mine'|'others'|'all'|'none'; search?: string } | undefined;
      return (
        <div className="h-full overflow-auto">
          <Suspense fallback={embedFallback}>
            <EmbeddedTasksPage readonly={mode === 'snapshot'} initialFilters={filter} />
          </Suspense>
        </div>
      );
    };
    map['berichte.embed']       = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedReportsPage /></Suspense></div>);
    map['plantafel.embed']      = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedPlantafelPage /></Suspense></div>);
    map['material.embed']       = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedMaterialPage /></Suspense></div>);
    map['bibliothek.embed']     = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedBibliothekPage hideHeader /></Suspense></div>);
    map['adressbuch.embed']     = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedAdressbuchPage embedded /></Suspense></div>);
    map['kunden.embed']         = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedKundenPage embedded /></Suspense></div>);
    map['zeiterfassung.embed']  = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedZeiterfassungPage embedded /></Suspense></div>);
    map['bauberichteV2.embed']  = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedBaustellePage embedded /></Suspense></div>);
    map['auswertungen.embed']   = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedAuswertungenPage embedded /></Suspense></div>);
    map['verwaltung.glossar']         = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedGlossarAdminPage /></Suspense></div>);
    map['verwaltung.vorlagen']        = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedVorlagenListPage /></Suspense></div>);
    map['verwaltung.mobile-homepage'] = () => (<div className="h-full overflow-auto"><Suspense fallback={embedFallback}><EmbeddedMobileHomepagePage /></Suspense></div>);

    // Team-Monat-Widget (Joerg 2026-05-30): eigener Datenfluss, unabhaengig
    // vom /dashboard/fuehrung-Roundtrip — daher hier vor dem if(data)-Block.
    map['fuehrung.team-monat']  = () => <TeamMonatWidget />;
    map['heute.team-karte']     = () => <TagesKarteWidget />;

    if (data) {
      map['fuehrung.mannschaft']        = () => renderMannschaft(data);
      map['fuehrung.laufend']           = () => renderLaufendJetzt(data);
      map['fuehrung.offen-summary']     = () => renderOffenSummary(data);
      map['fuehrung.eigene-berichte']   = () => renderEigeneBerichte(data);
      map['fuehrung.fremde-berichte']   = () => renderFremdeBerichte(data);
      map['fuehrung.fremde-verif']      = () => renderFremdeVerifiziert(data);
      map['fuehrung.abwesenheiten']     = () => renderAbwesenheiten(data, approveItem, openReject);
      map['fuehrung.sonstiges']         = () => renderSonstiges(data, approveItem, openReject);
      map['fuehrung.rechn-entwurf']     = () => renderRechnungenEntwurf(data);
      map['fuehrung.rechn-genehmigung'] = () => renderRechnungenGenehmigung(data);
      map['fuehrung.stunden-woche']     = () => renderStundenWoche(data);
      map['fuehrung.top-kunden']        = () => renderTopKunden(data);
      map['fuehrung.material-monat']    = () => renderMaterialMonat(data);
      map['fuehrung.letzte-berichte']   = () => renderLetzteBerichte(data);
      map['fuehrung.status-wechsel']    = () => renderStatusWechsel(data);
      map['fuehrung.wochenvorschau']    = () => renderWochenVorschau(data, region);
      map['fuehrung.schnellzugriffe']   = () => renderSchnellzugriffe();
    }

    // Heute-Widgets — generischer HomeCard-Render, Daten aus /home/today.
    // Card-ID matched 1:1 zum Backend-CardId in HOME_CARDS.
    const heuteWidgets: Array<{ slug: string; cardId: string; fallback: string }> = [
      { slug: 'heute.my-active-tasks',   cardId: 'my-active-tasks',   fallback: 'Meine aktiven Aufgaben' },
      { slug: 'heute.active-jobs-today', cardId: 'active-jobs-today', fallback: 'Heute aktive Baustellen' },
      { slug: 'heute.open-approvals',    cardId: 'open-approvals',    fallback: 'Offene Genehmigungen' },
      { slug: 'heute.to-dispatch',       cardId: 'to-dispatch',       fallback: 'Zu disponierende Aufgaben' },
      { slug: 'heute.new-reports',       cardId: 'new-reports',       fallback: 'Neue Berichte (24 h)' },
      { slug: 'heute.my-clock-status',   cardId: 'my-clock-status',   fallback: 'Mein Stempel-Status' },
      { slug: 'heute.my-unverified-reports',        cardId: 'my-unverified-reports',        fallback: 'Meine unverifizierten Berichte' },
      { slug: 'heute.my-tasks-today-without-report', cardId: 'my-tasks-today-without-report', fallback: 'Heute bearbeitet — Bericht offen' },
      { slug: 'heute.my-tasks-without-report',       cardId: 'my-tasks-without-report',       fallback: 'Bericht offen — Zeit erfasst' },
    ];
    for (const w of heuteWidgets) {
      map[w.slug] = () => (
        <HomeCard card={todayCards?.get(w.cardId)} fallbackTitle={w.fallback} />
      );
    }

    return map;
  }, [data, region, todayCards]);

  const inUserPageMode = !!userPageId || inTemplateMode;

  // Loader-Bedingungen:
  //   pageId-Modus: warte auf userPage (sonst landet WidgetGrid im Legacy-pageSlug-Modus)
  //   sonst:        warte auf erstes Dashboard-Fetch
  if (inUserPageMode && !userPage && !userPageError) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (!inUserPageMode && loading && !data) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  }
  if (error && !data) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">{error}</div>
      </div>
    );
  }
  if (userPageError) {
    return (
      <div className="max-w-3xl mx-auto p-6">
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-700 dark:text-red-300">{userPageError}</div>
      </div>
    );
  }
  // Im pageId-Modus kann data null sein (z.B. nicht-GF mit reiner Heute-Seite) —
  // weiter rendern; fuehrung.*-Widgets fallen aus dem widgetMap.
  if (!inUserPageMode && !data) return null;

  const today = new Date();
  const heuteLabel = today.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
  const title = userPage ? userPage.name : 'Geschäftsführung — Tagesüberblick';

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-4">
      {actAsUserId && (
        <div className="px-4 py-2 rounded-lg bg-purple-500/15 border border-purple-500/40 text-purple-200 text-sm flex items-center justify-between gap-3 flex-wrap">
          <div>
            <strong>Admin-Modus:</strong> Du bearbeitest die Seiten eines anderen Users (User-ID {actAsUserId.slice(0, 8)}…). Speichern landet in dessen Layout.
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/user-pages')}
            className="px-2 py-1 rounded bg-purple-500/30 hover:bg-purple-500/50 text-purple-100 text-xs"
          >
            Anderen User wählen
          </button>
        </div>
      )}
      {inTemplateMode && (
        <div className="px-4 py-2 rounded-lg bg-amber-500/15 border border-amber-500/40 text-amber-200 text-sm flex items-center justify-between gap-3 flex-wrap">
          <div>
            <strong>Vorlage bearbeiten:</strong> Änderungen wirken sich nur auf neue Seiten aus, die aus dieser Vorlage erstellt werden. Bestehende User-Pages bleiben unverändert (außer der User klickt „Auf Vorlage zurücksetzen").
          </div>
          <button
            type="button"
            onClick={() => navigate('/admin/page-templates')}
            className="px-2 py-1 rounded bg-amber-500/30 hover:bg-amber-500/50 text-amber-100 text-xs"
          >
            Vorlagen-Liste
          </button>
        </div>
      )}
      <header className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          <p className="text-sm text-muted-foreground mt-1">{heuteLabel}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Layout-Customization nur im pageId-Modus (persoenliche Seiten).
              Phase 2d (2026-05-27): PUT /page-layout/:slug ist entfernt — der
              Legacy-pageSlug-Modus ist read-only, deshalb Edit-Button + Verwalten-
              Button + Save-Tenant-Default-Checkbox dort nicht mehr anbieten. */}
          {inUserPageMode && editing && (
            <>
              <button
                onClick={() => { setCancelTick((t) => t + 1); setEditing(false); }}
                className="px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm min-h-[40px]"
              >
                <X size={14} className="inline mr-1" /> Abbrechen
              </button>
              <button
                onClick={() => { setSaveTick((t) => t + 1); }}
                className="px-3 py-2 rounded-lg bg-brand-600 text-primary-foreground hover:bg-brand-500 text-sm min-h-[40px]"
              >
                <Save size={14} className="inline mr-1" /> Speichern
              </button>
            </>
          )}
          {inUserPageMode && !editing && (
            <>
              {/* Verwalten (rename/delete/reset) funktioniert seit 2026-06-08
                  auch im Admin-Mode — PageSettingsDialog routet bei
                  actAsUserId auf /admin/users/:userId/pages-Endpoints.
                  Im Template-Modus ausblenden — Metadaten werden in der
                  Vorlagen-Liste editiert, nicht im Layout-Editor. */}
              {!inTemplateMode && (
                <button
                  onClick={() => setSettingsOpen(true)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-default text-foreground hover:bg-muted text-sm min-h-[40px]"
                  title="Seite umbenennen oder löschen"
                >
                  <Settings size={14} /> Verwalten
                </button>
              )}
              <button
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-slate-200 hover:bg-slate-700 text-sm min-h-[40px]"
                title="Anordnung der Widgets anpassen"
              >
                <LayoutIcon size={14} /> Layout anpassen
              </button>
            </>
          )}
        </div>
      </header>

      {inUserPageMode && editing && (
        <div className="mb-3 px-4 py-3 rounded-lg bg-amber-500/10 border border-amber-500/40 text-amber-300 text-sm">
          <div className="font-medium mb-1">Anpassen-Modus aktiv</div>
          <ul className="text-xs space-y-0.5 list-disc list-inside text-amber-300/90">
            <li>Widgets per Drag&amp;Drop sortieren — Resize-Griff unten rechts an jedem Widget.</li>
            <li>Im Widget-Picker (Knopf „Widgets verwalten" oben rechts) Doppelklick fügt ein Widget hinzu.</li>
            <li>Vergiss <strong>Speichern</strong> nicht — sonst gehen Änderungen verloren.</li>
          </ul>
        </div>
      )}

      {inUserPageMode && userPage ? (
        <WidgetGrid
          key={(inTemplateMode ? 'tpl:' : 'page:') + userPage.id}
          pageId={inTemplateMode ? undefined : userPage.id}
          templateId={inTemplateMode ? userPage.id : undefined}
          actAsUserId={actAsUserId ?? undefined}
          initialLayout={userPage.layout_json}
          widgetMap={widgetMap}
          editing={editing}
          saveTick={saveTick}
          cancelTick={cancelTick}
          onSaved={() => { setEditing(false); }}
        />
      ) : (
        // Legacy-pageSlug-Modus: read-only Fallback aus DEFAULT_LAYOUTS, kein
        // Edit (siehe Phase-2d-Header-Block oben — Buttons werden nur im
        // pageId-Modus angezeigt).
        <WidgetGrid
          pageSlug="fuehrung"
          widgetMap={widgetMap}
          editing={false}
        />
      )}

      {inUserPageMode && userPage && !inTemplateMode && (
        <PageSettingsDialog
          open={settingsOpen}
          pageId={userPage.id}
          initialName={userPage.name}
          hasTemplate={!!userPage.source_template_id}
          actAsUserId={actAsUserId ?? undefined}
          onClose={() => setSettingsOpen(false)}
          onRenamed={(newName) => {
            setUserPage({ ...userPage, name: newName });
            // Tab-Bar wird beim naechsten Reload neu geladen; hard-reload via Navigate auf gleiche Route
            if (!actAsUserId) window.dispatchEvent(new Event('my-pages:changed'));
          }}
          onDeleted={() => {
            setSettingsOpen(false);
            if (actAsUserId) {
              // Admin: zurueck zur Pages-Liste des Ziel-Users
              navigate(`/admin/user-pages/${actAsUserId}`);
            } else {
              window.dispatchEvent(new Event('my-pages:changed'));
              navigate('/uebersicht/heute');
            }
          }}
          onReset={() => {
            // Seite neu laden, damit das Layout-JSON aus der Vorlage frisch
            // an WidgetGrid uebergeben wird.
            window.location.reload();
          }}
        />
      )}

      {rejectTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !rejectBusy && setRejectTarget(null)}>
          <div className="bg-card border border-default rounded-xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold">
              {rejectTarget.kind === 'absence' ? 'Antrag ablehnen' : 'Korrektur ablehnen'}
            </h3>
            <p className="text-sm text-muted-foreground">
              Bitte gib einen Kommentar mit — der Antragsteller sieht ihn als Begründung.
            </p>
            <textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              rows={4}
              placeholder="Begründung…"
              className="w-full px-3 py-2 rounded-lg bg-background border border-default text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-red-500/40"
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setRejectTarget(null)}
                disabled={rejectBusy}
                className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground"
              >Abbrechen</button>
              <button
                type="button"
                onClick={confirmReject}
                disabled={rejectBusy || !rejectComment.trim()}
                className="px-3 py-1.5 rounded-lg text-sm bg-red-500/20 text-red-200 hover:bg-red-500/40 disabled:opacity-50"
              >Ablehnen</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Widget-Renderer (17 Stueck) — Card-JSX-Bodies, Daten als Parameter
// ============================================================================

/**
 * PauseRow — Zeile fuer „in Pause"-Mitarbeiter mit Startzeit + Live-Dauer
 * (Joerg 2026-07-23). Tickt 1×/min via eigenem setInterval, damit die Dauer
 * ohne Widget-Refresh nachlaeuft. Fallback auf einfache "in Pause"-Zeile,
 * wenn `since` fehlt (alter Payload).
 */
function PauseRow({ name, since }: { name: string; since: string | null }) {
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!since) return;
    const id = setInterval(() => setTick((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, [since]);

  let suffix = 'in Pause';
  if (since) {
    const start = new Date(since);
    const startLabel = start.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const diffMin = Math.max(0, Math.floor((Date.now() - start.getTime()) / 60000));
    const durLabel = diffMin < 60
      ? `${diffMin} min`
      : `${Math.floor(diffMin / 60)}h ${String(diffMin % 60).padStart(2, '0')}m`;
    suffix = `in Pause seit ${startLabel} (${durLabel})`;
  }
  return (
    <li className="flex items-center gap-2 text-amber-500">
      {/* Bernsteinfarbener Punkt = MA ist eingestempelt, aber gerade in der
          Pause (Joerg 2026-06-15, Word-Fund 4). Startzeit + Dauer erweitert
          2026-07-23 fuer bessere GF-Sicht. */}
      <span className="w-2 h-2 rounded-full bg-amber-400" /> {name} <span className="text-xs">{suffix}</span>
    </li>
  );
}

export function renderMannschaft(data: DashboardData) {
  const { mannschaft } = data;
  // Joerg 2026-05-30: aktiv-Block als Tabelle mit Arbeit/Pause/Aufgaben-Spalten
  // (Live-Minuten, offene Sessions bis now()). Fallback auf aktivNamen-Liste,
  // falls aktivList aus irgendeinem Grund nicht da ist (alter Cache, Modul aus).
  const aktivRows = mannschaft.aktivList ?? (mannschaft.aktivNamen ?? []).map((n) => ({
    userId: `legacy-${n}`, name: n, color: null as string | null,
    bruttoMinutesToday: 0, breakMinutesToday: 0, taskMinutesToday: 0,
  }));
  return (
    <Card
      title="Mannschaft" icon={Users} to="/uebersicht/mitarbeiter"
      info="Heute aktive Mitarbeiter. 'Aktiv' = eingeplante Personen, die heute schon gestempelt haben. Spalten Arbeit/Pause/Aufgaben sind Live-Werte heute (offene Sessions bis jetzt). Liste darunter: krank/Urlaub und Personen ohne Stempel-Eintrag heute."
    >
      <div className="px-4 py-3 space-y-3">
        <div className="flex items-baseline gap-2">
          <span className="text-3xl font-bold tabular-nums text-foreground">{mannschaft.aktiv}</span>
          <span className="text-sm text-muted-foreground">/ {mannschaft.eingeplant} aktiv</span>
        </div>
        {aktivRows.length > 0 && (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="px-1 py-1 text-left font-medium">Mitarbeiter</th>
                <th className="px-1 py-1 text-right font-medium">Arbeit</th>
                <th className="px-1 py-1 text-right font-medium">Pause</th>
                <th className="px-1 py-1 text-right font-medium">Aufgaben</th>
              </tr>
            </thead>
            <tbody>
              {aktivRows.map((a) => (
                <tr key={`a-${a.userId}`} className="border-t border-default/30">
                  <td className="px-1 py-1 text-foreground truncate" title={`${a.name} — Pin-Farbe`}>
                    {/* Punkt in der gleichen Farbe wie der Pin auf der Tages-Karte.
                        Joerg 2026-07-24: Farbe kommt aus addressbook.persons.color
                        (Kelly-22-Palette, kollisionsfrei); Fallback ist der alte
                        Hash-Weg. Legacy-aktivNamen-Path hat keine color, daher
                        weiter Hash. */}
                    <span
                      className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle"
                      style={{ backgroundColor: colorForPerson({ id: a.userId, color: a.color ?? null }) }}
                    />{a.name}
                    {/* Jörg 2026-09-11: Online-Status (PWA-Presence) direkt in
                        der Mannschaft — ● online / ○ offline seit X (Tooltip). */}
                    <PresenceDot userId={a.userId} />
                  </td>
                  <td className="px-1 py-1 text-right tabular-nums text-foreground">{fmtMinutes(a.bruttoMinutesToday)}</td>
                  <td className="px-1 py-1 text-right tabular-nums text-muted-foreground">{fmtMinutes(a.breakMinutesToday)}</td>
                  <td className="px-1 py-1 text-right tabular-nums text-foreground">{fmtMinutes(a.taskMinutesToday)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {(mannschaft.krank.length > 0 || mannschaft.urlaub.length > 0 || (mannschaft.inPause?.length ?? 0) > 0 || mannschaft.nichtGestempelt.length > 0) && (
          <ul className="space-y-1 text-sm border-t border-default/30 pt-2">
            {mannschaft.krank.map((a) => (
              <AbsenceRow key={`k-${a.absenceId}`} absence={a} />
            ))}
            {mannschaft.urlaub.map((a) => (
              <AbsenceRow key={`u-${a.absenceId}`} absence={a} />
            ))}
            {(mannschaft.inPause ?? []).map((p) => (
              <PauseRow key={`p-${p.name}`} name={p.name} since={p.since} />
            ))}
            {mannschaft.nichtGestempelt.map((n) => (
              <li key={`n-${n}`} className="flex items-center gap-2 text-muted-foreground">
                {/* Weisser Punkt = MA ist eingeplant aber heute noch nicht
                    gestempelt (Joerg 2026-05-30). Hellgrauer Ring sorgt fuer
                    Kontrast auch auf hellem Hintergrund. */}
                <span className="w-2 h-2 rounded-full bg-white border border-slate-400" /> {n} <span className="text-xs">n. gestempelt</span>
              </li>
            ))}
          </ul>
        )}
        {aktivRows.length === 0 && mannschaft.krank.length === 0 && mannschaft.urlaub.length === 0 && (mannschaft.inPause?.length ?? 0) === 0 && mannschaft.nichtGestempelt.length === 0 && (
          <p className="text-xs text-muted-foreground">alle vollzählig.</p>
        )}
      </div>
    </Card>
  );
}

export function renderLaufendJetzt(data: DashboardData) {
  const { laufendeTasks } = data;
  return (
    <Card
      title="Laufend jetzt" icon={MapPin} count={laufendeTasks.length} to="/uebersicht/plantafel"
      info="Aufgaben mit aktuell laufendem Timer (Mitarbeiter ist auf der Aufgabe drin, keine Pause). Anzeige der jüngsten 6 nach Start-Zeit. Klick auf einen Eintrag öffnet die Aufgaben-Detail-Seite. Für die GPS-Kartensicht den Link unten in die Plantafel benutzen."
    >
      {laufendeTasks.length === 0 ? (
        <EmptyHint text="Aktuell keine Aufgaben in Arbeit." />
      ) : (
        <>
          <ul className="divide-y divide-default max-h-60 overflow-y-auto">
            {laufendeTasks.slice(0, 6).map((t) => (
              <ItemRow key={t.id} to={`/m/bauberichteV2/tasks/${t.id}`} title={t.title} sub={[t.customer, t.user].filter(Boolean).join(' · ') || undefined} />
            ))}
          </ul>
          <div className="px-4 py-2 border-t border-default text-xs">
            <Link to="/m/plantafel" className="text-primary hover:underline">Karte in Plantafel öffnen →</Link>
          </div>
        </>
      )}
    </Card>
  );
}

export function renderOffenSummary(data: DashboardData) {
  const { offenSummary } = data;
  // Jede Zeile als Direkt-Link auf die zustaendige Sicht. Joerg-Wunsch (Alex
  // 2026-05-26): „Wenn ich auf eine der Zeilen klicke, passiert nichts. ...
  // sollte als direkte Verlinkung dienen". Ziel pro Zeile gewaehlt nach
  // Folge-Action: Berichte zur Berichts-Liste, Antraege/Korrekturen in die
  // jeweiligen Zeiterfassungs-Sub-Tabs, Aufgaben ohne Owner in die Aufgaben-Liste.
  const rows: Array<{ label: string; count: number; to: string }> = [
    { label: 'Eigene Berichte',      count: offenSummary.eigeneBerichte,    to: '/uebersicht/berichte' },
    { label: 'Fremde Berichte',      count: offenSummary.fremdeBerichte,    to: '/uebersicht/berichte' },
    { label: 'Abwesenheits-Anträge', count: offenSummary.urlaubsantraege,   to: '/uebersicht/zeiterfassung?tab=antraege' },
    { label: 'Zeitkorrekturen',      count: offenSummary.zeitkorrekturen,   to: '/uebersicht/zeiterfassung?tab=antraege&sub=corrections' },
    { label: 'Aufgaben ohne Owner',  count: offenSummary.aufgabenOhneOwner, to: '/uebersicht/aufgaben?owner=none' },
  ];
  return (
    <Card
      title="Offen gesamt" icon={AlertCircle} count={offenSummary.total}
      info="Summe der wartenden Approval-Items: eigene + fremde unverifizierte Berichte, offene Zeitkorrektur-Anträge, Aufgaben ohne zugewiesenen Mitarbeiter."
    >
      <div className="px-2 py-2 space-y-0.5">
        {/* Counter=0 → kein Link, kein Chevron (Joerg/Kiara 2026-05-27:
            „eigene Berichte Counter liegt bei 0, mit Klick werden alle Berichte
            angezeigt — sollte bei 0 keine Berichte auflisten"). Wir
            deaktivieren stattdessen die Verlinkung, damit der User nicht in
            eine nicht-gefilterte Liste fliegt. */}
        {rows.map((r) => {
          const inner = (
            <>
              <span className={`transition-colors ${r.count > 0 ? 'text-muted-foreground group-hover/row:text-foreground' : 'text-muted-foreground/60'}`}>{r.label}</span>
              <span className="flex items-center gap-1">
                <span className={`font-medium tabular-nums ${r.count === 0 ? 'text-muted-foreground/60' : ''}`}>{r.count}</span>
                {r.count > 0 && <ChevronRight size={14} className="text-muted-foreground/50 group-hover/row:text-foreground transition-colors" />}
              </span>
            </>
          );
          if (r.count === 0) {
            return (
              <div key={r.label} className="flex items-center justify-between text-sm px-2 py-1.5 select-none">
                {inner}
              </div>
            );
          }
          return (
            <Link
              key={r.label}
              to={r.to}
              className="flex items-center justify-between text-sm px-2 py-1.5 rounded-md hover:bg-background/50 transition-colors group/row"
            >
              {inner}
            </Link>
          );
        })}
      </div>
    </Card>
  );
}

function renderEigeneBerichte(data: DashboardData) {
  // Counter zeigt das echte Total (data.offenSummary.eigeneBerichte), nicht
  // die Listenlaenge — Liste ist auf LIMIT 5 begrenzt (Joerg 2026-05-28).
  return (
    <Card
      title="Eigene Berichte zu prüfen" icon={Stamp} count={data.offenSummary.eigeneBerichte} to="/uebersicht/berichte"
      info="Berichte die du selbst geschrieben hast (eingereicht, aber von dir noch nicht verifiziert). Ältester zuerst. Klick öffnet den Bericht zur Verifizierung."
    >
      {data.eigeneBerichteOffen.length === 0 ? <EmptyHint text="Nichts offen." /> : (
        <ItemList>
          {data.eigeneBerichteOffen.map((r) => (
            <ItemRow key={r.id} to={`/m/bauberichteV2/reports/${r.id}`} title={r.title} sub={[r.customer, r.submittedAt ? formatTime(r.submittedAt) : null].filter(Boolean).join(' · ') || undefined} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderFremdeBerichte(data: DashboardData) {
  return (
    <Card
      title="Fremde Berichte offen" icon={FileText} count={data.offenSummary.fremdeBerichte} to="/uebersicht/berichte"
      info="Berichte anderer Mitarbeiter, die du als Geschäftsführung abnehmen/verifizieren musst. Eingereicht, noch nicht verifiziert, fremder Autor. Ältester zuerst."
    >
      {data.fremdeBerichteOffen.length === 0 ? <EmptyHint text="Alle Berichte abgenommen." /> : (
        <ItemList>
          {data.fremdeBerichteOffen.map((r) => (
            <ItemRow key={r.id} to={`/m/bauberichteV2/reports/${r.id}`} title={r.title} sub={[r.customer, r.user, r.submittedAt ? formatTime(r.submittedAt) : null].filter(Boolean).join(' · ') || undefined} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderFremdeVerifiziert(data: DashboardData) {
  return (
    <Card
      title="Fremde Berichte OK (Audit)" icon={CheckCircle2} count={data.fremdeBerichteVerifiziertTotal} to="/uebersicht/berichte"
      info="Audit-Spalte: zuletzt verifizierte Berichte anderer Mitarbeiter. Nicht zum Bearbeiten — zeigt wer wann was abgenommen hat."
    >
      {data.fremdeBerichteVerifiziert.length === 0 ? <EmptyHint text="Nichts kürzlich verifiziert." /> : (
        <ItemList>
          {data.fremdeBerichteVerifiziert.map((r) => (
            <ItemRow key={r.id} to={`/m/bauberichteV2/reports/${r.id}`} title={r.title} sub={[r.user, r.verifiedAt ? formatTime(r.verifiedAt) : null].filter(Boolean).join(' · ') || undefined} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderAbwesenheiten(
  data: DashboardData,
  onApprove: (id: string, kind: 'absence' | 'correction') => void,
  onReject: (id: string, kind: 'absence' | 'correction') => void,
) {
  return (
    <Card
      title="Abwesenheits-Anträge" icon={Plane} count={data.offenSummary.urlaubsantraege} to="/m/zeiterfassung/antraege"
      info="Offene Abwesenheits-Anträge (Urlaub, Sonderurlaub, unbezahlt, Schule, sonstiges). Krankmeldungen sind nicht dabei — sie werden gemeldet und nicht genehmigt."
    >
      {data.urlaubsantraege.length === 0 ? <EmptyHint text="Keine offenen Anträge." /> : (
        <ul className="text-sm">
          {data.urlaubsantraege.map((a) => (
            <li key={a.id} className="px-3 py-2 border-b border-default/40 last:border-b-0">
              <div className="flex items-center justify-between gap-2">
                <Link to={`/m/zeiterfassung/antraege/${a.id}`} className="flex-1 min-w-0 hover:bg-card/40 rounded -mx-1 px-1 py-0.5 transition-colors">
                  <div className="font-medium text-foreground truncate">{a.user ?? '—'}</div>
                  <div className="text-xs text-muted-foreground">
                    {a.type} · {formatDateShort(a.fromDate)} – {formatDateShort(a.toDate)}
                    {/* ABW 2026-08-28: Stunden-Zeitausgleich sichtbar machen */}
                    {a.durationMinutes != null && (
                      <> · {(a.durationMinutes / 60).toLocaleString('de-DE', { maximumFractionDigits: 2 })} Std</>
                    )}
                  </div>
                </Link>
                <div className="flex items-center gap-1 shrink-0">
                  <button
                    type="button"
                    onClick={() => onApprove(a.id, 'absence')}
                    className="px-2 py-0.5 rounded text-[11px] bg-brand-600/20 text-brand-200 hover:bg-brand-600/40 transition-colors"
                    title="Antrag genehmigen"
                  >✓ Genehmigen</button>
                  <button
                    type="button"
                    onClick={() => onReject(a.id, 'absence')}
                    className="px-2 py-0.5 rounded text-[11px] bg-red-500/15 text-red-300 hover:bg-red-500/30 transition-colors"
                    title="Antrag ablehnen (mit Kommentar)"
                  >✗ Ablehnen</button>
                </div>
              </div>
              {a.type === 'urlaub' && (
                <div className="mt-1 flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">
                    {a.urlaubstage} Tag{a.urlaubstage === 1 ? '' : 'e'}
                  </span>
                  {a.resturlaubTage !== null && (
                    <span className={a.ueberzieht ? 'text-red-300 font-medium' : 'text-muted-foreground'}>
                      · Resturlaub: {a.resturlaubTage}
                      {a.ueberzieht && ' — Antrag zieht über!'}
                    </span>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function renderSonstiges(
  data: DashboardData,
  onApprove: (id: string, kind: 'absence' | 'correction') => void,
  onReject: (id: string, kind: 'absence' | 'correction') => void,
) {
  return (
    <Card
      title="Sonstiges" icon={Clock}
      info="Restliche Approval-Themen: offene Zeitkorrektur-Anträge und Aufgaben ohne zugewiesenen Mitarbeiter."
    >
      <div className="px-4 py-3 space-y-3 text-sm">
        <div>
          <div className="font-medium text-foreground mb-1 flex items-center justify-between">
            <span>Zeitkorrekturen</span>
            <span className="tabular-nums text-muted-foreground">{data.offenSummary.zeitkorrekturen}</span>
          </div>
          {data.zeitkorrekturen.length === 0 ? <p className="text-xs text-muted-foreground">keine offen</p> : (
            <ul className="text-xs space-y-2">
              {data.zeitkorrekturen.slice(0, 3).map((c) => {
                const reqType = c.requestedType ? (STAMP_TYPE_LABEL[c.requestedType] ?? c.requestedType) : null;
                const reqTime = c.requestedOccurredAt ? formatDateTimeShort(c.requestedOccurredAt) : null;
                const origType = c.originalType ? (STAMP_TYPE_LABEL[c.originalType] ?? c.originalType) : null;
                const origTime = c.originalOccurredAt ? formatDateTimeShort(c.originalOccurredAt) : null;
                return (
                  <li key={c.id} className="space-y-0.5">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-medium text-foreground truncate">{c.user ?? '—'}</span>
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          type="button"
                          onClick={() => onApprove(c.id, 'correction')}
                          className="px-2 py-0.5 rounded text-[11px] bg-brand-600/20 text-brand-200 hover:bg-brand-600/40 transition-colors"
                          title="Korrektur genehmigen"
                        >✓ Genehmigen</button>
                        <button
                          type="button"
                          onClick={() => onReject(c.id, 'correction')}
                          className="px-2 py-0.5 rounded text-[11px] bg-red-500/15 text-red-300 hover:bg-red-500/30 transition-colors"
                          title="Korrektur ablehnen (mit Kommentar)"
                        >✗ Ablehnen</button>
                      </div>
                    </div>
                    {(reqType || reqTime) && (
                      <div className="text-muted-foreground">
                        soll: {reqType ?? '—'}{reqTime ? ` · ${reqTime}` : ''}
                      </div>
                    )}
                    {(origType || origTime) && (
                      <div className="text-muted-foreground">
                        war: {origType ?? '—'}{origTime ? ` · ${origTime}` : ''}
                      </div>
                    )}
                    {c.reason && (
                      <div className="text-muted-foreground italic truncate" title={c.reason}>„{c.reason}"</div>
                    )}
                    <div className="text-[10px] text-muted-foreground/70">
                      eingereicht {formatDateTimeShort(c.createdAt)}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <Link to="/uebersicht/zeiterfassung?tab=antraege&sub=corrections" className="text-xs text-primary hover:underline">öffnen →</Link>
        </div>
        <div className="border-t border-default pt-3">
          <div className="font-medium text-foreground mb-1 flex items-center justify-between">
            <span>Aufgaben ohne Owner</span>
            <span className="tabular-nums text-muted-foreground">{data.offenSummary.aufgabenOhneOwner}</span>
          </div>
          {data.aufgabenOhneOwner.length === 0 ? <p className="text-xs text-muted-foreground">alle zugewiesen</p> : (
            <ul className="text-xs text-muted-foreground space-y-0.5">
              {data.aufgabenOhneOwner.slice(0, 3).map((t) => (<li key={t.id} className="truncate">• {t.title}</li>))}
            </ul>
          )}
          <Link to="/uebersicht/aufgaben?owner=none" className="text-xs text-primary hover:underline">öffnen →</Link>
        </div>
      </div>
    </Card>
  );
}

/**
 * Team-Monat-Widget (Joerg 2026-05-30): Monats-Summen pro aktivem Mitarbeiter
 * — Brutto / Pause / Netto / Soll / Saldo. Eigener Datenfluss (Monat-State +
 * eigener Endpoint /portal/dashboard/team-month), unabhaengig vom dashboard-
 * fuehrung-Roundtrip. Klick auf eine Zeile springt auf den persoenlichen
 * Monats-Detail-View des Mitarbeiters (Zeiterfassung-Modul).
 */
interface TeamMonthMember {
  userId: string;
  name: string;
  bruttoMinutes: number;
  breakMinutes: number;
  nettoMinutes: number;
  sollMinutes: number;
  saldoMonatMinutes: number;
  saldoGesamtMinutes: number;
}
interface TeamMonthData {
  month: string;
  monatFrom: string;
  monatTo: string;
  gesamtFrom: string;
  gesamtTo: string;
  members: TeamMonthMember[];
}

function currentYmIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function shiftYm(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map((s) => parseInt(s, 10));
  const d = new Date(y!, (m ?? 1) - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}
function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map((s) => parseInt(s, 10));
  const d = new Date(y!, (m ?? 1) - 1, 1);
  return d.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
}

function TeamMonatWidget() {
  const [ym, setYm] = useState(currentYmIso());
  const [data, setData] = useState<TeamMonthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);
  // Auszahlung-Modal-State (Joerg 2026-05-30)
  const [payTarget, setPayTarget] = useState<TeamMonthMember | null>(null);
  const [payDate, setPayDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [payHours, setPayHours] = useState<number>(8);
  const [payReason, setPayReason] = useState('');
  const [payBusy, setPayBusy] = useState(false);
  const [payError, setPayError] = useState<string | null>(null);
  // Download-Busy (pro MA)
  const [downloadBusy, setDownloadBusy] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    api.get<{ success: boolean; data: TeamMonthData; error?: string }>(
      `/portal/dashboard/team-month?ym=${ym}`,
    )
      .then((r) => {
        if (cancelled) return;
        if (!r.success) {
          setError(r.error ?? 'Fehler beim Laden');
          setData(null);
        } else {
          setData(r.data);
        }
      })
      .catch((e) => { if (!cancelled) setError((e as Error).message ?? 'Fehler beim Laden'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ym, reloadTick]);

  function openPay(m: TeamMonthMember): void {
    setPayTarget(m);
    setPayDate(new Date().toISOString().slice(0, 10));
    setPayHours(8);
    setPayReason('');
    setPayError(null);
  }

  async function submitPay(): Promise<void> {
    if (!payTarget) return;
    if (!payDate || !payHours || payHours <= 0) {
      setPayError('Datum und Stunden (positiv) sind Pflicht.'); return;
    }
    setPayBusy(true); setPayError(null);
    try {
      const r = await api.post<{ success: boolean; error?: string }>(
        '/portal/dashboard/saldo-adjustments',
        { userId: payTarget.userId, day: payDate, hours: payHours, reason: payReason.trim() || undefined },
      );
      if (!r.success) { setPayError(r.error ?? 'Buchung fehlgeschlagen'); return; }
      setPayTarget(null);
      setReloadTick((t) => t + 1);
    } catch (e) {
      setPayError((e as Error).message ?? 'Buchung fehlgeschlagen');
    } finally {
      setPayBusy(false);
    }
  }

  async function downloadCsv(m: TeamMonthMember): Promise<void> {
    setDownloadBusy(m.userId);
    try {
      const res = await fetch(`/api/portal/dashboard/employee-month-csv?userId=${m.userId}&ym=${ym}`, {
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `monatskonto-${m.name.replace(/[^a-zA-Z0-9]/g, '_')}-${ym}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`CSV-Download fehlgeschlagen: ${(e as Error).message}`);
    } finally {
      setDownloadBusy(null);
    }
  }

  return (
    <Card
      title={`Team-Monat — ${monthLabel(ym)}`}
      icon={Stamp}
      info={'Pro aktivem Mitarbeiter: Brutto/Pause/Netto/Soll des Monats, plus Saldo-Monat und Saldo-Gesamt (Lebenszeit-Konto vom Eintrittsdatum bis Monatsende). Aktionen pro Zeile: Auszahlung buchen + Monatskonto als CSV.'}
    >
      <div className="flex flex-col h-full min-h-0">
        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-default/40">
          <button
            type="button"
            onClick={() => setYm(shiftYm(ym, -1))}
            className="p-1 rounded hover:bg-card/60 text-muted-foreground"
            title="vorheriger Monat"
            aria-label="vorheriger Monat"
          ><ChevronLeft size={14} /></button>
          <span className="text-xs font-medium tabular-nums text-foreground">{ym}</span>
          <button
            type="button"
            onClick={() => setYm(shiftYm(ym, 1))}
            className="p-1 rounded hover:bg-card/60 text-muted-foreground"
            title="naechster Monat"
            aria-label="naechster Monat"
          ><ChevronRight size={14} /></button>
        </div>
        <div className="flex-1 min-h-0 overflow-auto">
          {error ? (
            <p className="px-3 py-4 text-xs text-red-300">{error}</p>
          ) : loading && !data ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
          ) : !data || data.members.length === 0 ? (
            <EmptyHint text="Keine Mitarbeiter im Monat." />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-card/30 text-muted-foreground sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Mitarbeiter</th>
                  <th className="px-2 py-1.5 text-right font-medium">Brutto</th>
                  <th className="px-2 py-1.5 text-right font-medium">Pause</th>
                  <th className="px-2 py-1.5 text-right font-medium">Netto</th>
                  <th className="px-2 py-1.5 text-right font-medium">Soll</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Saldo dieses Monats">Saldo M</th>
                  <th className="px-2 py-1.5 text-right font-medium" title="Lebenszeit-Saldo bis Monatsende">Saldo G</th>
                  <th className="px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {data.members.map((m) => (
                  <tr key={m.userId} className="border-t border-default/30 hover:bg-card/40 transition-colors">
                    <td className="px-2 py-1.5 text-foreground truncate max-w-[160px]" title={m.name}>{m.name}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{fmtMinutes(m.bruttoMinutes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{fmtMinutes(m.breakMinutes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-foreground">{fmtMinutes(m.nettoMinutes)}</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-muted-foreground">{fmtMinutes(m.sollMinutes)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${m.saldoMonatMinutes < 0 ? 'text-red-400' : 'text-brand-300'}`}>
                      {fmtMinutes(m.saldoMonatMinutes)}
                    </td>
                    <td className={`px-2 py-1.5 text-right tabular-nums font-medium ${m.saldoGesamtMinutes < 0 ? 'text-red-400' : 'text-brand-300'}`}>
                      {fmtMinutes(m.saldoGesamtMinutes)}
                    </td>
                    <td className="px-2 py-1.5 whitespace-nowrap">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          onClick={() => openPay(m)}
                          className="p-1 rounded hover:bg-amber-700/30 text-amber-300"
                          title="Ueberstunden auszahlen"
                          aria-label={`${m.name}: Auszahlung buchen`}
                        ><Wallet size={12} /></button>
                        <button
                          type="button"
                          onClick={() => downloadCsv(m)}
                          disabled={downloadBusy === m.userId}
                          className="p-1 rounded hover:bg-card/60 text-muted-foreground disabled:opacity-50"
                          title="Monatskonto als CSV herunterladen"
                          aria-label={`${m.name}: Monat als CSV`}
                        >{downloadBusy === m.userId ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {payTarget && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => !payBusy && setPayTarget(null)}>
          <div className="bg-card border border-default rounded-xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <Wallet size={18} className="text-amber-300" /> Auszahlung — {payTarget.name}
            </h3>
            <p className="text-xs text-muted-foreground">
              Verbucht die angegebenen Stunden als <strong>Konto-Abzug</strong> (kind=auszahlung). Aktueller
              Gesamt-Saldo: <span className={payTarget.saldoGesamtMinutes < 0 ? 'text-red-400' : 'text-brand-300'}>{fmtMinutes(payTarget.saldoGesamtMinutes)}</span>
            </p>
            {payError && <p className="text-xs text-red-300">{payError}</p>}
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Datum</span>
                <input type="date" value={payDate} onChange={(e) => setPayDate(e.target.value)}
                       className="w-full bg-background border border-default rounded-lg px-3 py-2 text-foreground text-sm" />
              </label>
              <label className="text-sm">
                <span className="block text-xs text-muted-foreground mb-1">Stunden</span>
                <input type="number" min={0.25} step={0.25} value={payHours}
                       onChange={(e) => setPayHours(Number(e.target.value))}
                       className="w-full bg-background border border-default rounded-lg px-3 py-2 text-foreground text-sm" />
              </label>
            </div>
            <label className="text-sm block">
              <span className="block text-xs text-muted-foreground mb-1">Grund (optional)</span>
              <input type="text" value={payReason} onChange={(e) => setPayReason(e.target.value)}
                     placeholder="z.B. Mai-Auszahlung Plus-Stunden"
                     className="w-full bg-background border border-default rounded-lg px-3 py-2 text-foreground text-sm" />
            </label>
            <div className="flex justify-end gap-2 pt-2 border-t border-default">
              <button type="button" onClick={() => setPayTarget(null)} disabled={payBusy}
                      className="px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:bg-card/60">Abbrechen</button>
              <button type="button" onClick={submitPay} disabled={payBusy}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm bg-amber-700/40 border border-amber-700/60 text-amber-100 hover:bg-amber-700/60 disabled:opacity-60">
                {payBusy ? <Loader2 size={14} className="animate-spin" /> : <Wallet size={14} />} Auszahlung buchen
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

/**
 * Tages-Karte als Widget (Joerg 2026-05-30): wrappt TagesMapView aus dem
 * Plantafel-Modul mit fest `date=today`. Holt einmalig users (fuer stabile
 * Mitarbeiter-Farben) und tasks-count (fuer den „X von Y haben GPS"-Counter).
 * Die Karte selbst hat ihren eigenen 15s-Polling-Loop in TagesMapView.
 */
export function TagesKarteWidget() {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [users, setUsers] = useState<PlantafelUser[]>([]);
  const [totalTasksToday, setTotalTasksToday] = useState(0);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      api.get<{ success: boolean; data: PlantafelUser[]; error?: string }>('/modules/plantafel/users'),
      api.get<{ success: boolean; data: PlantafelTask[]; error?: string }>(`/modules/plantafel/tasks?from=${today}&to=${today}`),
    ])
      .then(([u, t]) => {
        if (cancelled) return;
        if (u.success) setUsers(u.data ?? []);
        if (t.success) setTotalTasksToday((t.data ?? []).length);
      })
      .catch((e) => { if (!cancelled) setError((e as Error).message ?? 'Fehler beim Laden'); });
    return () => { cancelled = true; };
  }, [today]);

  return (
    <Card
      title={`Team-Karte — heute (${formatDateShort(today)})`}
      icon={MapPin}
      info="Pin pro Aufgabe, fuer die heute ein GPS-Stempel existiert. Farbe pro Mitarbeiter. Popup zeigt Aufgabe, Kunde und letzten GPS-Zeitpunkt. Aktualisiert sich alle 15 Sekunden."
    >
      <div className="h-full min-h-0 p-2">
        {error ? (
          <p className="px-3 py-4 text-xs text-red-300">{error}</p>
        ) : (
          <Suspense fallback={<div className="h-full grid place-items-center text-muted-foreground text-xs"><Loader2 className="w-5 h-5 animate-spin" /></div>}>
            <LazyTagesMapView date={today} users={users} totalTasksToday={totalTasksToday} />
          </Suspense>
        )}
      </div>
    </Card>
  );
}

function renderRechnungenEntwurf(data: DashboardData) {
  return (
    <Card
      title="Rechnungen — Entwurf" icon={Receipt} count={data.rechnungenEntwurfTotal} to="/m/vorlagen/rechnung"
      info="Vom Büro vorbereitete Rechnungs-Drafts, noch nicht zur Genehmigung eingereicht."
    >
      {data.rechnungenEntwurf.length === 0 ? <EmptyHint text="Keine Entwürfe vorhanden." /> : (
        <ItemList>
          {data.rechnungenEntwurf.map((d) => (
            <ItemRow key={d.id} to={`/m/vorlagen/drafts/${d.id}`} title={d.title}
              sub={[d.customer, d.createdBy].filter(Boolean).join(' · ') || undefined}
              right={d.brutto != null ? <span className="text-xs tabular-nums text-foreground font-medium">{formatEuro(d.brutto)}</span> : undefined} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderRechnungenGenehmigung(data: DashboardData) {
  return (
    <Card
      title="Rechnungen — zur Genehmigung" icon={AlertCircle} count={data.rechnungenGenehmigungTotal} to="/m/vorlagen/rechnung"
      info="Rechnungs-Drafts im Status 'eingereicht' — warten auf deine Freigabe als Geschäftsführung."
    >
      {data.rechnungenGenehmigung.length === 0 ? <EmptyHint text="Nichts wartet auf Freigabe." /> : (
        <ItemList>
          {data.rechnungenGenehmigung.map((d) => (
            <ItemRow key={d.id} to={`/m/vorlagen/drafts/${d.id}`} title={d.title}
              sub={[d.customer, d.createdBy].filter(Boolean).join(' · ') || undefined}
              right={d.brutto != null ? <span className="text-xs tabular-nums text-amber-500 font-medium">{formatEuro(d.brutto)}</span> : undefined} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderStundenWoche(data: DashboardData) {
  return (
    <Card
      title="Stunden Woche" icon={TrendingUp} to="/uebersicht/auswertungen"
      info="Geleistete Stunden in der aktuellen Kalenderwoche. Balken-Trend zeigt die letzten 8 Wochen, Vorjahresvergleich darunter."
    >
      <StundenSparkline data={data.stundenWoche} />
    </Card>
  );
}

function renderTopKunden(data: DashboardData) {
  return (
    <Card
      title="Top 5 Kunden (Stunden Monat)" icon={Briefcase} to="/m/disposition/kunden"
      info="Die fünf Kunden mit den meisten geleisteten Stunden im laufenden Monat. Aggregiert aus Zeit-Einträgen ohne Pausen."
    >
      <Bars items={data.topKundenStunden.map((k) => ({ label: k.name, value: k.stunden, suffix: `${formatStunden(k.stunden)} h` }))} />
    </Card>
  );
}

function renderMaterialMonat(data: DashboardData) {
  return (
    <Card
      title="Material — Monat" icon={Package} to="/m/disposition/materialien"
      info="Top-Material-Positionen des laufenden Monats nach Kosten. Aggregiert aus Material-Posten aller Berichte."
    >
      <Bars items={data.materialMonat.map((m) => ({
        label: m.name, value: m.kosten,
        suffix: `${m.menge.toLocaleString('de-DE', { maximumFractionDigits: 1 })} ${m.einheit ?? ''} · ${formatEuro(m.kosten)}`,
      }))} />
    </Card>
  );
}

function renderLetzteBerichte(data: DashboardData) {
  return (
    <Card
      title="Letzte 10 Berichte" icon={FileText} to="/uebersicht/berichte"
      info="Die zehn jüngsten Berichte, neueste oben. Klick öffnet die Detail-Sicht des Berichts."
    >
      {data.letzte10Berichte.length === 0 ? <EmptyHint text="Noch keine Berichte." /> : (
        <ItemList>
          {data.letzte10Berichte.map((r) => (
            <ItemRow key={r.id} to={`/m/bauberichteV2/reports/${r.id}`} title={r.title}
              sub={[r.customer, r.user, formatTime(r.createdAt)].filter(Boolean).join(' · ')} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderStatusWechsel(data: DashboardData) {
  return (
    <Card
      title="Letzte Aufgaben-Bewegungen" icon={Activity} to="/uebersicht/aufgaben"
      info="Die zehn jüngsten Aufgaben-Updates (nach updated_at sortiert, neueste oben)."
    >
      {data.letzteStatusWechsel.length === 0 ? <EmptyHint text="Keine Bewegung." /> : (
        <ItemList>
          {data.letzteStatusWechsel.map((t) => (
            <ItemRow key={t.id} to={`/m/bauberichteV2/tasks/${t.id}`} title={t.title}
              sub={`${t.status ?? '—'} · ${formatTime(t.updatedAt)}`} />
          ))}
        </ItemList>
      )}
    </Card>
  );
}

function renderWochenVorschau(data: DashboardData, region: RegionCode) {
  const y = new Date().getFullYear();
  const holidaysMap = new Map<string, string>();
  for (const yy of [y, y + 1]) for (const [k, v] of getHolidays(yy, region)) holidaysMap.set(k, v);
  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <Card title="Diese Woche" icon={Calendar} info="7-Tage-Vorschau mit Anzahl Aufgaben pro Tag, Abnahmen, Feiertage je Bundesland.">
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 p-3">
        {data.wochenVorschau.map((day) => {
          const isToday = day.date === todayIso;
          const holiday = holidaysMap.get(day.date);
          const date = new Date(day.date + 'T00:00:00');
          const weekday = date.toLocaleDateString('de-DE', { weekday: 'short' });
          const dayNum = date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' });
          return (
            <div key={day.date} className={`bg-card border rounded-lg p-3 text-sm ${isToday ? 'border-primary ring-1 ring-primary/40' : 'border-default'}`}>
              <div className={`flex items-center justify-between mb-2 ${isToday ? 'text-primary font-semibold' : 'text-foreground'}`}>
                <span className="text-xs uppercase tracking-wider">{weekday}</span>
                <span className="text-xs tabular-nums">{dayNum}</span>
              </div>
              <div className="text-2xl font-bold tabular-nums text-foreground">{day.count}</div>
              <div className="text-[11px] text-muted-foreground">Aufgaben</div>
              {holiday ? <div className="mt-2 text-[11px] text-red-500 truncate" title={holiday}>· {holiday}</div> : null}
              {day.abnahmen.length > 0 ? (
                <ul className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
                  {day.abnahmen.slice(0, 2).map((a) => (
                    <li key={a.id} className="truncate" title={`Abnahme: ${a.title}${a.customer ? ` (${a.customer})` : ''}`}>✓ {a.title}</li>
                  ))}
                  {day.abnahmen.length > 2 ? <li className="text-[10px]">+ {day.abnahmen.length - 2} weitere</li> : null}
                </ul>
              ) : null}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function renderSchnellzugriffe() {
  const links = [
    { to: '/uebersicht/plantafel',    label: 'Plantafel',    icon: Calendar },
    { to: '/uebersicht/auswertungen', label: 'Auswertungen', icon: BarChart3 },
    { to: '/admin/adressbuch',        label: 'Adressbuch',   icon: Users },
    { to: '/uebersicht/bibliothek',   label: 'Bibliothek',   icon: Library },
  ];
  return (
    <Card title="Schnellzugriffe" icon={ChevronRight} info="Schnelle Weiterleitungen zu Plantafel/Auswertungen/Adressbuch/Bibliothek.">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="bg-card border border-default rounded-lg p-4 flex items-center gap-3 hover:bg-background hover:border-primary/30 transition-colors">
            <l.icon size={20} className="text-primary flex-shrink-0" />
            <span className="text-sm font-medium text-foreground">{l.label}</span>
          </Link>
        ))}
      </div>
    </Card>
  );
}

// ============================================================================
// Shared UI-Bausteine (unveraendert aus pre-Widget-Picker-Version)
// ============================================================================

function Card({ title, icon: Icon, count, info, to, children, className = '' }: {
  title: string;
  icon?: React.ComponentType<{ size?: number; className?: string }>;
  count?: number;
  info?: string;
  to?: string;
  children: ReactNode;
  className?: string;
}) {
  const headerCls = to ? 'flex items-start justify-between gap-2 px-4 py-3 border-b border-default hover:bg-background/50 transition-colors group' : 'flex items-start justify-between gap-2 px-4 py-3 border-b border-default';
  const headerInner = (
    <>
      <div className="flex items-start gap-2 min-w-0">
        {Icon ? <Icon size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" /> : null}
        {/* Joerg/Alex 2026-05-26: keine truncate-Abschneidung. Lange Titel
            duerfen zweizeilig umbrechen — Tooltip-Fallback war schon Pflicht,
            aber „Eigene Bericht..." wirkt unprofessionell. */}
        <span className="text-sm font-medium text-foreground leading-snug break-words [overflow-wrap:anywhere] line-clamp-2">{title}</span>
        {info ? <InfoTooltip text={info} /> : null}
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        {count != null && <span className="text-xs tabular-nums text-muted-foreground">{count}</span>}
        {to ? <ChevronRight size={14} className="text-muted-foreground group-hover:text-foreground transition-colors" /> : null}
      </div>
    </>
  );
  return (
    <div className={`h-full flex flex-col ${className}`}>
      {to ? <Link to={to} className={headerCls}>{headerInner}</Link> : <div className={headerCls}>{headerInner}</div>}
      <div className="flex-1 min-h-0">{children}</div>
    </div>
  );
}

function EmptyHint({ text = 'Keine Einträge' }: { text?: string }) {
  return <div className="px-4 py-6 text-center text-xs text-muted-foreground italic">{text}</div>;
}

/**
 * Section-Header (Joerg/Alex 2026-05-27, #5): visueller Trenner mit
 * Zwischenueberschrift zwischen Widget-Gruppen. Nimmt eine ganze Zeile
 * (h=1 ≈ 70 px) ein und rendert ohne Card-Border — der Drag-Handle vom
 * Edit-Mode bleibt drueber.
 */
function renderSectionHeader(label: string) {
  return (
    <div className="h-full flex items-end px-2 pb-2">
      <h2 className="text-xs uppercase tracking-wide text-muted-foreground border-b border-default flex-1 pb-1">
        {label}
      </h2>
    </div>
  );
}

function ItemList({ children, footer }: { children: ReactNode; footer?: ReactNode }) {
  return (
    <>
      <ul className="divide-y divide-default max-h-72 overflow-y-auto">{children}</ul>
      {footer}
    </>
  );
}

function ItemRow({ to, title, sub, right }: { to?: string; title: string; sub?: string; right?: ReactNode }) {
  const cls = to ? 'block px-4 py-2 hover:bg-background/50 transition-colors' : 'block px-4 py-2';
  const inner = (
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="text-sm text-foreground truncate">{title}</div>
        {sub ? <div className="text-xs text-muted-foreground truncate">{sub}</div> : null}
      </div>
      {right ? <div className="flex-shrink-0">{right}</div> : null}
    </div>
  );
  return (
    <li>
      {to ? <Link to={to} className={cls}>{inner}</Link> : <div className={cls}>{inner}</div>}
    </li>
  );
}

function StundenSparkline({ data }: { data: DashboardData['stundenWoche'] }) {
  const points = data.sparkline;
  const max = Math.max(1, ...points.map((p) => p.stunden));
  return (
    <div className="px-4 py-4 space-y-3">
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-bold tabular-nums text-foreground">{formatStunden(data.stunden)}</span>
        <span className="text-sm text-muted-foreground">h diese Woche</span>
      </div>
      {points.length > 0 ? (
        <div className="flex items-end gap-1 h-12">
          {points.map((p, i) => {
            const h = Math.max(2, Math.round((p.stunden / max) * 100));
            const isCurrent = i === points.length - 1;
            return (
              <div key={p.week} style={{ height: `${h}%` }} className={`flex-1 rounded-t ${isCurrent ? 'bg-primary' : 'bg-primary/30'}`} title={`${p.week}: ${formatStunden(p.stunden)} h`} />
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">Keine Stunden-Daten verfügbar.</p>
      )}
      {data.vorjahrStunden != null && data.vorjahrStunden > 0 ? (
        <p className="text-xs text-muted-foreground">
          Vorjahr gleiche KW: {formatStunden(data.vorjahrStunden)} h{' '}
          <span className={data.stunden >= data.vorjahrStunden ? 'text-brand-500' : 'text-red-500'}>
            ({data.stunden >= data.vorjahrStunden ? '+' : ''}{Math.round(((data.stunden - data.vorjahrStunden) / data.vorjahrStunden) * 100)} %)
          </span>
        </p>
      ) : null}
    </div>
  );
}

function Bars({ items }: { items: Array<{ label: string; value: number; suffix?: string }> }) {
  if (items.length === 0) return <EmptyHint text="Keine Daten im Zeitraum." />;
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="px-4 py-3 space-y-2">
      {items.map((it, idx) => {
        const pct = Math.round((it.value / max) * 100);
        return (
          <li key={`${it.label}-${idx}`} className="space-y-1">
            <div className="flex items-center justify-between gap-2 text-xs">
              <span className="text-foreground truncate">{it.label}</span>
              <span className="tabular-nums text-muted-foreground flex-shrink-0">{it.suffix}</span>
            </div>
            <div className="h-1.5 rounded-full bg-card overflow-hidden border border-default">
              <div className="h-full bg-primary" style={{ width: `${pct}%` }} />
            </div>
          </li>
        );
      })}
    </ul>
  );
}
